/**
 * Crisp chat utility functions with fallback support
 * for when Crisp is blocked by ad blockers or content blockers
 */

declare global {
  interface Window {
    $crisp?: {
      do: (action: string, args?: any[]) => void;
      is?: (state: string) => boolean;
      get?: (key: string) => any;
      set?: (key: string, value: any) => void;
    };
  }
}

const CRISP_WEBSITE_ID = '6697d752-94f4-4764-aca9-348d5ae30597';

/**
 * Check if Crisp is available and loaded
 */
export function isCrispAvailable(): boolean {
  return typeof window !== 'undefined' && 
         window.$crisp !== undefined && 
         typeof window.$crisp.do === 'function';
}

/**
 * Open Crisp chat with fallback to direct URL
 * @param message Optional message to pre-fill
 * @param userEmail Optional user email to override automatic detection
 * @param userName Optional user name to override automatic detection
 */
export function openCrispChat(message?: string, userEmail?: string, userName?: string): void {
  if (isCrispAvailable()) {
    try {
      // Try to use the native Crisp API
      window.$crisp!.do('chat:show');
      window.$crisp!.do('chat:open');
      
      // Set user email if provided and not already set
      if (userEmail && typeof window.$crisp!.set === 'function') {
        try {
          window.$crisp!.set('user:email', [userEmail]);
        } catch {
          // Ignore if setting email fails
        }
      }
      
      // Set user nickname if provided and not already set
      if (userName && typeof window.$crisp!.set === 'function') {
        try {
          window.$crisp!.set('user:nickname', [userName]);
        } catch {
          // Ignore if setting nickname fails
        }
      }
      
      if (message) {
        window.$crisp!.do('message:send', ['text', message]);
      }
    } catch (error) {
      console.warn('Error opening Crisp chat, falling back to direct URL:', error);
      openCrispFallback(message, userEmail, userName);
    }
  } else {
    // Crisp is not available (probably blocked), use fallback
    console.info('Crisp not available, opening direct chat URL');
    openCrispFallback(message, userEmail, userName);
  }
}

/**
 * Fallback function to open Crisp chat in a new window/tab
 * @param message Optional message to pre-fill
 * @param userEmail Optional user email to override automatic detection
 * @param userName Optional user name to override automatic detection
 */
function openCrispFallback(message?: string, userEmail?: string, userName?: string): void {
  let url = `https://go.crisp.chat/chat/embed/?website_id=${CRISP_WEBSITE_ID}`;
  
  // Get user information - use provided values or detect automatically
  const user = getUserInfo();
  const email = userEmail || user.email;
  const nickname = userName || user.nickname;
  
  if (email) {
    url += `&user_email=${encodeURIComponent(email)}`;
  }
  if (nickname) {
    url += `&user_nickname=${encodeURIComponent(nickname)}`;
  }
  
  // Add pre-filled message if provided
  if (message) {
    // Note: Crisp's embed URL doesn't support pre-filled messages directly,
    // but we can add it as a session data parameter that might be picked up
    url += `&session_data=${encodeURIComponent(JSON.stringify({ message }))}`;
  }
  
  // Open in a new tab/window
  window.open(url, '_blank', 'width=400,height=600');
}

/**
 * Get user information from the page if available
 */
function getUserInfo(): { email?: string; nickname?: string } {
  // Try to get user info from various sources
  const info: { email?: string; nickname?: string } = {};
  
  if (typeof window !== 'undefined') {
    // Method 1: Try to get from the Inertia page div data attribute
    // Inertia stores the initial page data in a data attribute on the app element
    try {
      const appElement = document.getElementById('app');
      if (appElement) {
        const pageDataAttr = appElement.getAttribute('data-page');
        if (pageDataAttr) {
          const pageData = JSON.parse(pageDataAttr);
          if (pageData?.props?.auth?.user) {
            info.email = pageData.props.auth.user.email;
            info.nickname = pageData.props.auth.user.name || pageData.props.auth.user.company;
            // Return early if we found the user info
            if (info.email) return info;
          }
        }
      }
    } catch (error) {
      console.debug('Could not parse Inertia page data:', error);
    }
    
    // Method 2: Check for meta tags (if the backend adds them)
    const emailMeta = document.querySelector('meta[name="user-email"]');
    const nameMeta = document.querySelector('meta[name="user-name"]');
    
    if (!info.email && emailMeta) {
      info.email = emailMeta.getAttribute('content') || undefined;
    }
    if (!info.nickname && nameMeta) {
      info.nickname = nameMeta.getAttribute('content') || undefined;
    }
    
    // Method 3: Try to get from CSRF token cookie (email might be encoded there in some apps)
    // This is a fallback and might not work in all cases
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]');
      if (csrfMeta) {
        // The CSRF token itself doesn't contain user info, but we keep this for future enhancement
      }
    } catch {
      // Ignore
    }
  }
  
  return info;
}

/**
 * Hide Crisp chat widget (only works if Crisp is available)
 */
export function hideCrispChat(): void {
  if (isCrispAvailable()) {
    try {
      window.$crisp!.do('chat:hide');
    } catch (error) {
      console.warn('Error hiding Crisp chat:', error);
    }
  }
}

/**
 * Check if Crisp chat is currently open
 */
export function isCrispChatOpen(): boolean {
  if (isCrispAvailable() && typeof window.$crisp!.is === 'function') {
    try {
      return window.$crisp!.is('chat:opened');
    } catch {
      return false;
    }
  }
  return false;
}